import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { artefactVersionText } from '../lib/artefactVersionText';
import { lineDiff } from '../lib/lineDiff';
import { clientApprovalTypeLabel } from '../lib/clientApprovals';
import {
  type ClientApprovalDecisionResult, type ClientApprovalVersion,
  NO_BASELINE_COPY, NO_LONGER_AWAITING_COPY, REASON_MIN, decisionErrorMessage, progressLabel,
} from '../lib/clientApprovalPortal';

/**
 * #3187 APPROVE.4 — the version view (design v1.1 §5.4): the rendered
 * artefact, the diff against the last client-approved version, then Approve or
 * Request changes. Mounted at /client/approvals/:artefactId/requests/:requestId
 * inside ClientApprovalsLiveRoute (dark until 3b) and ActionRoute (#3248).
 *
 * The same view serves a full login and an email-link action session. For an
 * action session it is headed "Approving as <displayName>" from GET
 * /auth/action-session (#3248 ruling: the fixation mitigation, so a person who
 * redeemed someone else's link sees whose session it is). Opening it never
 * decides anything: the only write is the POST behind the submit button (R-7).
 *
 * apiFetch only (#3250). The decision posts against the artefact the LOADED
 * request names, never the URL's artefactId, and the page refuses a request
 * whose artefact differs from the URL (or, for an action session, whose
 * version differs from the session's version claim): the API stays the
 * authority, this keeps the page from showing one thing and deciding another.
 */

interface ActionProbe { displayName?: unknown; versionId?: unknown; expiresAt?: unknown; target?: { id?: unknown } | null }

/**
 * The email-link session as the page last read it (Codex R2-1). The probe
 * carries no user id, so the session is told apart by its name, its target,
 * its version claim and its expiry: another approver's link redeemed in
 * another tab, or the same link redeemed again, changes at least one of them.
 */
interface SessionSeen { displayName: string; targetId: string | null; versionId: string | null; expiresAt: string | null }

function sessionSeen(probe: ActionProbe | null | undefined): SessionSeen | null {
  const displayName = typeof probe?.displayName === 'string' && probe.displayName !== '' ? probe.displayName : null;
  if (displayName === null) return null;
  const targetId = typeof probe?.target?.id === 'string' ? probe.target.id : null;
  return {
    displayName,
    targetId,
    versionId: typeof probe?.versionId === 'string' ? probe.versionId : null,
    expiresAt: typeof probe?.expiresAt === 'string' ? probe.expiresAt : null,
  };
}

const sameId = (a: string | null, b: string | null) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
const sameSession = (a: SessionSeen, b: SessionSeen) =>
  a.displayName === b.displayName && sameId(a.targetId, b.targetId) && sameId(a.versionId, b.versionId) && a.expiresAt === b.expiresAt;

type Decision = 'APPROVE' | 'REQUEST_CHANGES';

type Load =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'ready'; data: ClientApprovalVersion; session: SessionSeen | null; viaSession: boolean };

const SESSION_CHANGED_COPY = (name: string) =>
  `This page is now signed in as ${name}. Nothing was submitted. Check the name above, then submit again if that's you.`;
const SESSION_UNCONFIRMED_COPY = "We couldn't confirm whose session this is. Nothing was submitted. Please try again.";

/**
 * One page instance per request (R1 P2-1): a decision still in flight for the
 * previous request, its result, and the comment typed for it never carry into
 * the next one when the route params change under a full login (ActionRoute
 * renders the same element for both).
 */
export default function ClientApprovalVersionPage() {
  const { artefactId, requestId } = useParams<{ artefactId: string; requestId: string }>();
  return <VersionView key={`${artefactId ?? ''}/${requestId ?? ''}`} artefactId={artefactId} requestId={requestId} />;
}

function VersionView({ artefactId, requestId }: { artefactId?: string; requestId?: string }) {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [mode, setMode] = useState<Decision>('APPROVE');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The result carries the decision actually sent (Codex R2-4), not the radio's state.
  const [result, setResult] = useState<{ sent: Decision; res: ClientApprovalDecisionResult } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad({ state: 'loading' });
    setResult(null);
    setError(null);
    const fullLogin = localStorage.getItem('blueprint_token') !== null;
    const versionPath = `/client-approval-requests/${encodeURIComponent(requestId ?? '')}/version`;
    // The email-link session (the cookie): probe for whose session it is, then
    // read the version without any stored Bearer token.
    const viaSessionLoad = async () => {
      const session = sessionSeen(await apiFetch<ActionProbe>('/auth/action-session', { omitBearer: true }));
      const data = await apiFetch<ClientApprovalVersion>(versionPath, { omitBearer: true });
      return { data, session };
    };
    (async () => {
      try {
        let loaded: { data: ClientApprovalVersion; session: SessionSeen | null };
        let viaSession = !fullLogin;
        if (fullLogin) {
          try {
            loaded = { data: await apiFetch<ClientApprovalVersion>(versionPath), session: null };
          } catch (err) {
            // R1 P2-3: a stored token that has expired (401) must not hide a
            // valid email-link session behind "nothing to decide".
            if (!(err instanceof ApiError && err.status === 401)) throw err;
            loaded = await viaSessionLoad();
            viaSession = true;
          }
        } else {
          loaded = await viaSessionLoad();
        }
        if (cancelled) return;
        const { data, session } = loaded;
        const sameArtefact = !!artefactId && data.artefactId.toLowerCase() === artefactId.toLowerCase();
        const sameVersion = !viaSession || (session?.versionId != null && sameId(session.versionId, data.version.id));
        if (!sameArtefact || !sameVersion || (viaSession && session === null)) { setLoad({ state: 'unavailable' }); return; }
        setLoad({ state: 'ready', data, session, viaSession });
      } catch {
        if (!cancelled) setLoad({ state: 'unavailable' });
      }
    })();
    return () => { cancelled = true; };
  }, [artefactId, requestId]);

  const ready = load.state === 'ready' ? load.data : null;
  const body = useMemo(() => artefactVersionText(ready?.version.notes), [ready]);
  const diff = useMemo(
    () => (ready?.lastClientApproved ? lineDiff(artefactVersionText(ready.lastClientApproved.notes), body) : null),
    [ready, body],
  );

  if (load.state === 'loading') return <div data-testid="approval-version-loading" style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (load.state === 'unavailable') {
    return (
      <div data-testid="approval-version-unavailable" style={{ maxWidth: 640, margin: '0 auto', padding: 32 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{NO_LONGER_AWAITING_COPY}</h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary, #6b7280)' }}>There is nothing to decide here.</p>
      </div>
    );
  }

  const data = load.data;
  const viaSession = load.viaSession;
  const shown = load.session;
  const open = data.status === 'OPEN';

  async function submit() {
    const sent = mode;
    const trimmed = comment.trim();
    if (sent === 'REQUEST_CHANGES' && trimmed.length < REASON_MIN) {
      setError(`Tell the team what needs to change (at least ${REASON_MIN} characters).`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Codex R2-1: the cookie is shared by every tab, so another link
      // redeemed since this page loaded would decide as someone else under
      // this page's "Approving as" line. Re-read the session immediately
      // before posting; if it is not the one shown, post nothing.
      if (viaSession && shown) {
        let now: SessionSeen | null = null;
        try {
          now = sessionSeen(await apiFetch<ActionProbe>('/auth/action-session', { omitBearer: true }));
        } catch { /* left null: unconfirmed */ }
        if (now === null) { setError(SESSION_UNCONFIRMED_COPY); return; }
        if (!sameSession(now, shown)) {
          // Still a session for this artefact and version: re-head the page
          // with the current name and require a fresh click. Otherwise this
          // session cannot decide what the page shows.
          if (sameId(now.targetId, data.artefactId) && sameId(now.versionId, data.version.id)) {
            setLoad({ state: 'ready', data, session: now, viaSession });
            setError(SESSION_CHANGED_COPY(now.displayName));
          } else {
            setLoad({ state: 'unavailable' });
          }
          return;
        }
      }
      const res = await apiFetch<ClientApprovalDecisionResult>(`/artefacts/${encodeURIComponent(data.artefactId)}/client-approval`, {
        method: 'POST',
        // R1 P2-2: name the request and version this page showed, and (Codex
        // R2-1) the approver it was headed with, so the route (3b) can refuse
        // a decision on a version, or by a session, the approver never saw.
        body: JSON.stringify({
          ...(sent === 'REQUEST_CHANGES' ? { action: 'REQUEST_CHANGES', reason: trimmed } : { action: 'APPROVE' }),
          requestId: data.requestId, versionId: data.version.id,
          ...(viaSession && shown ? { actingAs: { displayName: shown.displayName, sessionExpiresAt: shown.expiresAt } } : {}),
        }),
        omitBearer: viaSession,
      });
      setResult({ sent, res });
    } catch (err) {
      setError(decisionErrorMessage(err instanceof ApiError ? err : null));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: 32 }}>
      {shown !== null && (
        <div data-testid="approving-as" role="note" style={{
          marginBottom: 16, padding: '10px 12px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--surface-alt)', fontSize: 14,
        }}>
          Approving as <strong>{shown.displayName}</strong>. If this isn&apos;t you, close this page.
        </div>
      )}
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{data.title}</h1>
      <div data-testid="approval-version-meta" style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginBottom: 24 }}>
        {clientApprovalTypeLabel(data.artefactType)} · version {data.version.version}
      </div>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>This version</h2>
        <div data-testid="approval-version-content" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
          {body ? <MarkdownRenderer disableMermaid className="prose prose-sm max-w-none">{body}</MarkdownRenderer> : <em>This version has no content.</em>}
        </div>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
          {data.lastClientApproved ? `Changes since version ${data.lastClientApproved.version} (last approved by you and your team)` : 'Changes'}
        </h2>
        {diff === null ? (
          <p data-testid="approval-diff-none" style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>{NO_BASELINE_COPY}</p>
        ) : (
          <pre data-testid="approval-diff" style={{
            margin: 0, padding: 12, border: '1px solid var(--border)', borderRadius: 8, fontSize: 12,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 480, overflow: 'auto',
          }}>
            {diff.map((l, i) => (
              <div key={i} data-diff={l.kind} style={{
                background: l.kind === 'added' ? 'rgba(34,197,94,0.12)' : l.kind === 'removed' ? 'rgba(239,68,68,0.12)' : 'transparent',
              }}>
                {l.kind === 'added' ? '+ ' : l.kind === 'removed' ? '- ' : '  '}{l.text}
              </div>
            ))}
          </pre>
        )}
      </section>

      <section data-testid="approval-decision">
        {result ? (
          <div data-testid="approval-decision-result" role="status" style={{ color: '#16a34a', fontWeight: 500, fontSize: 14 }}>
            {result.sent === 'REQUEST_CHANGES' ? 'Changes requested. The team has been notified.' : 'Approved.'}{' '}
            {typeof result.res.approvedCount === 'number' && typeof result.res.approverCount === 'number'
              && `${progressLabel(result.res.approvedCount, result.res.approverCount)}.`}
          </div>
        ) : !open ? (
          <p data-testid="approval-decision-closed" style={{ fontSize: 14 }}>{NO_LONGER_AWAITING_COPY}</p>
        ) : (
          <>
            <div role="radiogroup" aria-label="Your decision" style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input type="radio" name="decision" data-testid="decision-approve" checked={mode === 'APPROVE'} disabled={submitting}
                  onChange={() => { setMode('APPROVE'); setError(null); }} />
                Approve
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input type="radio" name="decision" data-testid="decision-request-changes" checked={mode === 'REQUEST_CHANGES'} disabled={submitting}
                  onChange={() => { setMode('REQUEST_CHANGES'); setError(null); }} />
                Request changes
              </label>
            </div>
            {mode === 'REQUEST_CHANGES' && (
              <textarea
                data-testid="decision-comment"
                aria-label="What needs to change"
                placeholder={`What needs to change? (at least ${REASON_MIN} characters)`}
                value={comment}
                disabled={submitting}
                onChange={(e) => setComment(e.target.value)}
                style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, marginBottom: 12 }}
              />
            )}
            {error && <div data-testid="decision-error" role="alert" style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button
              type="button"
              data-testid="decision-submit"
              disabled={submitting}
              onClick={() => { void submit(); }}
              style={{
                padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: 13,
                backgroundColor: mode === 'APPROVE' ? '#7c3aed' : '#f59e0b',
              }}
            >
              {submitting ? 'Submitting...' : mode === 'APPROVE' ? 'Approve' : 'Request changes'}
            </button>
          </>
        )}
      </section>
    </div>
  );
}
